import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./QuoteSlideshow.module.scss";
import QuoteSlideshowProps from "./QuoteSlideshowProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import IconViewer from "components/IconViewer/IconViewer";
import Quote from "./components/Quote/Quote";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import isBrowserContext from "helpers/isBrowserContext";

const QuoteSlideshow: FunctionComponent<QuoteSlideshowProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const [quoteWidth, setQuoteWidth] = useState(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [elementHovered, setElementHovered] = useState<boolean>(false);

    //Slideshow Logic
    const onPreviousClick = () => {
        if (currentSlideIndex === 0) return;
        paginateSlide(-1);
    };

    const onNextClick = () => {
        if (props.content.quotes) {
            if (props.content.quotes?.length === currentSlideIndex + 1) return;
        }
        paginateSlide(1);
    };

    const paginateSlide = (nextSlideIndex: number) => {
        setCurrentSlideIndex(currentSlideIndex + nextSlideIndex);
    };

    const swipeConfidenceThreshold = 10000;
    const swipePower = (offset: number, velocity: number) => {
        return Math.abs(offset) * velocity;
    };

    const handleDrag = (e:MouseEvent | TouchEvent | PointerEvent, { offset, velocity }:any) => {
        const swipe = swipePower(offset.x, velocity.x);

        if (swipe < -swipeConfidenceThreshold) {
            onNextClick();
        }
        else if (swipe > swipeConfidenceThreshold) {
            onPreviousClick();
        }
    };

    const handlePaginationKeyDown = (e:any, index:number) => {
        if(e.key === "Enter") {
            setCurrentSlideIndex(index);
        }
    };

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (elementHovered === false) {
                if ((currentSlideIndex + 1) > (props.content.quotes.length - 1)) {
                    setCurrentSlideIndex(0);
                }
                else {
                    setCurrentSlideIndex(currentSlideIndex + 1);
                }
            }
        }, 4000);

        return () => clearInterval(interval);
    }, [currentSlideIndex, elementHovered]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }} onMouseOver={() => setElementHovered(true)} onMouseOut={() => setElementHovered(false)}>
            <div className={styles.inner}>
                <div className={styles.container}>
                    <motion.div animate={{ x: quoteWidth * currentSlideIndex }} transition={{ ease: "easeInOut", duration: 0.5 }} className={styles.quotes} >
                        {props.content.quotes && props.content.quotes.length > 0 ? props.content.quotes.map((quote)=> {
                            return (
                                <motion.div className={styles.quote_wrapper} drag="x" dragConstraints={{ left: 0, right: 0 }} onDragEnd={(e, { offset, velocity }) => handleDrag(e, { offset, velocity })} key={quote.contentUdi}>
                                    <Quote {...quote} setQuoteWidth={setQuoteWidth}/>
                                </motion.div>
                            );
                        }): null}
                    </motion.div>

                </div>
                <div aria-label={`Arrow Left ${currentSlideIndex === 0 ? "Disabled" : ""}`} className={combineClasses([styles.arrow_left, currentSlideIndex === 0 ? styles.disabled : undefined])} tabIndex={0} onClick={() => onPreviousClick()} onKeyDown={(e) => {if(e.key === "Enter") {onPreviousClick();}}}>
                    <IconViewer name="arrow-left" />
                </div>

                <div aria-label={`Arrow Left ${props.content.quotes?.length === currentSlideIndex + 1 ? "Disabled" : ""}`} className={combineClasses([styles.arrow_right, props.content.quotes?.length === currentSlideIndex + 1 ? styles.disabled : undefined])} tabIndex={0} onClick={() => onNextClick()} onKeyDown={(e) => {if(e.key === "Enter") {onNextClick();}}}>
                    <IconViewer name="arrow-right" />
                </div>

                <div className={styles.pagination}>
                    {props.content.quotes && props.content.quotes.length > 0 ? props.content.quotes.map((_, index)=> {
                        return (
                            <div key={index} aria-label={`Pagination Dot ${index + 1}`} className={styles.dot_wrapper} tabIndex={0} onClick={()=> setCurrentSlideIndex(index)} onKeyDown={(e) => handlePaginationKeyDown(e, index)}>
                                <motion.span animate={{ width: currentSlideIndex === index ? "2rem" : "1.4rem" }} transition={{ duration: 0.4 }} className={combineClasses([styles.dot, currentSlideIndex === index ? styles.active_dot : undefined])}></motion.span>
                            </div>
                        );
                    }): null}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default QuoteSlideshow;
